/**
 * The UI shell: main menu, pause menu, settings, credits, loading, and the rebinding screen.
 *
 * ## Plain DOM, and navigable three ways
 *
 * No framework (stack decision in CLAUDE.md). What that costs is a `MenuList` — about eighty
 * lines — and what it buys is that the menu is *the same object* however it is driven. A row is
 * focusable, so the browser's own focus handling gives keyboard navigation and screen readers for
 * free; the gamepad is polled and synthesised into the same focus moves; and the mouse just
 * clicks. One code path, three input devices, and no chance of the gamepad reaching a control the
 * keyboard cannot.
 *
 * The alternative — a bespoke selection index with its own highlight — is what most game menus do
 * and it is why most game menus cannot be tabbed through.
 *
 * ## Why the menus are one module
 *
 * They share a stack (`FLY` pushes the aircraft picker, `Esc` pops it), a look, and a navigation
 * model, and splitting them would mean exporting that stack. The screens themselves are small:
 * the whole shell is a handful of `MenuList`s and one settings form.
 *
 * `updateFixed` / `updateFrame`: {@link MenuShell.updateFrame} polls the gamepad for navigation
 * and does nothing at all while the menu is closed.
 */

import {
  ACTION_GROUPS,
  ACTION_LABELS,
  AXIS_IDS,
  AXIS_LABELS,
  BindingStore,
  conflicts,
  gamepadAxisLabel,
  gamepadButtonLabel,
  keyLabel,
  type ActionId,
  type AxisId,
} from '../input/bindings.js';
import { TIERS, type QualityMode } from '../core/quality.js';
import { PRODUCT_NAME } from '../brand.js';

/** Small numbers as words, for menu copy. Beyond this a numeral reads better anyway. */
const NUMBER_WORDS: Readonly<Record<number, string>> = Object.freeze({
  1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five', 6: 'six', 7: 'seven', 8: 'eight',
});
import { MODEL_CREDITS } from '../aircraft/modelCredits.js';
import {
  CAMERA_MODES,
  UNIT_SYSTEMS,
  type CameraMode,
  type Settings,
  type SettingsStore,
  type UnitSystem,
} from '../game/settings.js';

/** Which screen is showing. `none` means the simulator has the screen to itself. */
export type MenuScreen =
  | 'none'
  | 'main'
  | 'pause'
  | 'settings'
  | 'controls'
  | 'credits'
  | 'help'
  | 'aircraft'
  | 'loading';

/**
 * The attributions CLAUDE.md requires, verbatim.
 *
 * **Duplicated from the providers, and that is a known hazard.** `worldSystem.ts` builds the same
 * list from each `TileProvider.attribution` via `collectAttributions`, and this array is a second
 * copy that the credits screen renders. They must agree; adding an imagery source and forgetting
 * this line means shipping a data source with no credit on the screen that exists to credit it,
 * which for a licence-bearing source is a breach rather than an oversight. Pinned by
 * `attributions.test.ts`.
 */
export const ATTRIBUTIONS: readonly string[] = [
  'Elevation: Terrain Tiles by Mapzen/AWS Open Data (SRTM/NASA, GMTED, ETOPO1, and others)',
  'Imagery (United States): NAIP by USDA Farm Service Agency (public domain)',
  'Imagery: Sentinel-2 cloudless — s2maps.eu by EOX IT Services GmbH (contains modified Copernicus Sentinel data)',
  'Airport data: OurAirports.com (public domain)',
];

/** The help card: what a new pilot needs to get airborne, not the full binding table. */
export const HELP_ROWS: readonly { keys: string; label: string }[] = [
  { keys: 'W / S', label: 'Pitch — nose up and down' },
  { keys: 'A / D', label: 'Roll — left and right' },
  { keys: 'Q / E', label: 'Rudder' },
  { keys: '− / =', label: 'Throttle' },
  { keys: 'F / V', label: 'Flaps down and up' },
  { keys: 'B', label: 'Wheel brakes (hold)' },
  { keys: 'I', label: 'Starter (hold) · shut down when running' },
  { keys: ', / .', label: 'Elevator trim' },
  { keys: 'Z / X', label: 'Rudder trim' },
  { keys: '; / ’', label: 'Aileron trim' },
  { keys: '1 – 5', label: 'Cockpit · chase · fly-by · orbit · free camera' },
  { keys: 'C', label: 'Cycle camera' },
  { keys: '6', label: 'Screenshot — save or share the view' },
  { keys: 'O', label: 'Fighter HUD overlay' },
  { keys: 'Space', label: 'Fire (armed aircraft, gunnery mode)' },
  { keys: 'Tab', label: 'Cycle weapon — gun / missile' },
  { keys: '0', label: 'Gunnery: drone range → balloon canyon → off' },
  { keys: 'J', label: 'Auto-coordination (on by default)' },
  { keys: 'Y', label: 'Mouse yoke' },
  { keys: 'L', label: 'Airport picker' },
  { keys: 'T', label: 'Time and weather' },
  { keys: 'R', label: 'Respawn' },
  { keys: 'P', label: 'Pause' },
  { keys: 'M', label: 'Mute' },
  { keys: 'F3', label: 'Performance overlay' },
  { keys: 'Esc', label: 'Menu' },
];

export interface MenuShellOptions {
  settings: SettingsStore;
  bindings: BindingStore;
  /** Start flying. Called from FLY on the main menu and from RESUME on the pause menu. */
  onFly: () => void;
  onResume: () => void;
  onRestart: () => void;
  /** Switch aeroplane. The id comes from `aircraft/registry.ts`. */
  onChangeAircraft: (aircraftId: string) => void;
  /** Which aeroplane is loaded now, so the picker can mark it. */
  currentAircraftId: () => string;
  /** Everything that can be flown. From `aircraft/registry.ts`; the menu never hard-codes a fleet. */
  fleet: () => readonly { id: string; name: string; summary: string }[];
  /** Reload the guns and the rails. Null when the aircraft carries nothing. */
  onRearm: (() => void) | null;
  /** Start a course, or leave one with `null`. */
  onGunnery: (course: 'drones' | 'balloons' | null) => void;
  /** Is a course running? Drives the pause menu's wording. */
  gunneryActive: () => boolean;
  /** Does the loaded aeroplane carry anything at all? */
  armed: () => boolean;
  onQuitToMenu: () => void;
  /** Open the existing airport picker overlay. */
  onOpenSpawnPicker: () => void;
  /**
   * Play the UI click.
   *
   * One callback rather than a call at every button, because there are thirty-odd controls in
   * here and a list of thirty call sites is a list with one missing. See the constructor: a
   * single capture-phase listener on the menu root covers every button, toggle, choice and
   * rebinding row that exists now or is added later.
   */
  onSound?: () => void;
  parent?: HTMLElement;
}

/**
 * One navigable list.
 *
 * Rows are real focusable elements, so `Tab`, arrow keys, the mouse and a screen reader all work
 * on the same DOM. The class adds only what the browser does not: wrap-around on the arrow keys,
 * and a `focus()` on the first row when the screen opens so a gamepad has somewhere to start.
 */
class MenuList {
  readonly element = document.createElement('div');
  private readonly rows: HTMLButtonElement[] = [];

  constructor(className = 'menu__list') {
    this.element.className = className;
    this.element.addEventListener('keydown', (event) => this.onKeyDown(event));
  }

  add(label: string, onSelect: () => void, hint?: string): HTMLButtonElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'menu__item';
    const text = document.createElement('span');
    text.textContent = label;
    row.append(text);
    if (hint !== undefined) {
      const hintEl = document.createElement('span');
      hintEl.className = 'menu__hint';
      hintEl.textContent = hint;
      row.append(hintEl);
    }
    row.addEventListener('click', onSelect);
    this.element.append(row);
    this.rows.push(row);
    return row;
  }

  /** Move focus to the first row — where a gamepad or a freshly opened screen starts. */
  focusFirst(): void {
    this.rows.find((row) => !row.disabled)?.focus();
  }

  /** Step the focus. `delta` is +1 down, −1 up. Wraps. */
  move(delta: number): void {
    if (this.rows.length === 0) return;
    const active = document.activeElement;
    const index = this.rows.findIndex((row) => row === active);
    const next = ((index === -1 ? 0 : index + delta) + this.rows.length) % this.rows.length;
    this.rows[next].focus();
  }

  /** Activate whatever has focus. What the gamepad's A button does. */
  activate(): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && this.element.contains(active)) active.click();
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.move(-1);
    }
  }
}

export class MenuShell {
  readonly element: HTMLDivElement;

  private readonly options: MenuShellOptions;
  private readonly screens = new Map<MenuScreen, HTMLElement>();
  private readonly lists = new Map<MenuScreen, MenuList>();
  private screenValue: MenuScreen = 'main';
  /** The screen `Esc` goes back to — a stack, so settings knows whether it came from the menu. */
  private readonly history: MenuScreen[] = [];

  private readonly loadingBar: HTMLDivElement;
  private readonly loadingLabel: HTMLDivElement;
  private readonly loadingTitle: HTMLDivElement;

  /** Set while a rebinding row is waiting for a keypress. */
  private capturing: { kind: 'action'; id: ActionId } | { kind: 'axis'; id: AxisId } | null = null;
  private readonly controlsBody = document.createElement('div');

  /** Gamepad edge detection, so a held stick does not scroll the whole menu in one frame. */
  private padCooldownS = 0;
  private padWasPressed = false;

  constructor(options: MenuShellOptions) {
    this.options = options;
    this.element = document.createElement('div');
    this.element.className = 'menu';

    this.buildMain();
    this.buildPause();
    this.buildSettings();
    this.buildControls();
    this.buildCredits();
    this.buildHelp();
    this.buildAircraft();

    const loading = this.makeScreen('loading', 'Loading');
    this.loadingTitle = document.createElement('div');
    this.loadingTitle.className = 'menu__loadingTitle';
    this.loadingBar = document.createElement('div');
    this.loadingBar.className = 'menu__barFill';
    const track = document.createElement('div');
    track.className = 'menu__barTrack';
    track.append(this.loadingBar);
    this.loadingLabel = document.createElement('div');
    this.loadingLabel.className = 'menu__loadingLabel';
    loading.append(this.loadingTitle, track, this.loadingLabel);

    /*
     * The click, in one place. Capture phase so it is heard even when a handler stops
     * propagation, and filtered to real controls so that clicking the background of a screen is
     * silent — a click with no cause is worse than no click.
     */
    this.element.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (target instanceof HTMLButtonElement) options.onSound?.();
      },
      { capture: true },
    );
    this.element.addEventListener(
      'change',
      (event) => {
        if (event.target instanceof HTMLInputElement) options.onSound?.();
      },
      { capture: true },
    );

    (options.parent ?? document.body).append(this.element);
    /*
     * Constructed *closed*, and bootstrap decides which screen opens.
     *
     * This used to be `setScreen('main')`, which put an interactive main menu on screen the moment
     * the shell was built — before the renderer had compiled a pipeline and well before
     * `loop.start()`. On the WebGPU path the pre-compile is fast enough that nobody noticed; found
     * on the **WebGL2 fallback**, where compiling every program serially takes tens of seconds, so
     * the menu appeared over the boot splash — hiding the one thing that was explaining the wait —
     * and FLY dismissed it into a black screen with no progress and no explanation.
     *
     * The menu's z-index is above the splash's on purpose (the pause menu has to cover everything),
     * so the ordering could not be fixed in the stylesheet. Every path out of bootstrap sets a
     * screen, so nothing is lost by starting at none.
     */
    this.setScreen('none');
  }

  get screen(): MenuScreen {
    return this.screenValue;
  }

  /** True while the shell is covering the simulator — the loop pauses on this. */
  get isOpen(): boolean {
    return this.screenValue !== 'none';
  }

  /** True while a rebinding row is swallowing keystrokes. */
  get isCapturingBinding(): boolean {
    return this.capturing !== null;
  }

  setScreen(screen: MenuScreen): void {
    this.capturing = null;
    this.screenValue = screen;
    this.element.hidden = screen === 'none';
    // A screen that dims the world behind it versus one that replaces it: the pause menu should
    // show what you are going back to, and the main menu should not.
    this.element.classList.toggle('menu--overlay', screen === 'pause' || screen === 'loading');
    for (const [id, element] of this.screens) element.hidden = id !== screen;
    if (screen === 'controls') this.refreshControls();
    if (screen === 'settings') this.refreshSettings();
    if (screen === 'aircraft') this.refreshAircraft();
    this.lists.get(screen)?.focusFirst();
  }

  /** Push a screen, remembering where to come back to. */
  push(screen: MenuScreen): void {
    this.history.push(this.screenValue);
    this.setScreen(screen);
  }

  /** Go back one screen. Returns false at the root, so the caller can decide what `Esc` means. */
  back(): boolean {
    const previous = this.history.pop();
    if (previous === undefined) return false;
    this.setScreen(previous);
    return true;
  }

  /** Show the loading screen with a title. */
  showLoading(title: string): void {
    this.loadingTitle.textContent = title;
    this.setProgress(0, 'starting…');
    this.setScreen('loading');
  }

  /** Update the tile-prefetch bar. `fraction` is 0…1. */
  setProgress(fraction: number, label: string): void {
    const clamped = Math.min(1, Math.max(0, fraction));
    this.loadingBar.style.width = `${(clamped * 100).toFixed(1)}%`;
    if (this.loadingLabel.textContent !== label) this.loadingLabel.textContent = label;
  }

  /**
   * A key event, from the shell's own listener.
   *
   * Returns true when the shell consumed it — which is how the flight controls know to ignore a
   * `W` typed into the airport search box, and how a rebinding row swallows the key it is
   * capturing rather than triggering whatever that key currently does.
   */
  handleKeyDown(event: KeyboardEvent): boolean {
    if (this.capturing !== null) {
      event.preventDefault();
      const code = event.code;
      if (code === 'Escape') {
        this.capturing = null;
      } else if (this.capturing.kind === 'action') {
        this.options.bindings.setAction(this.capturing.id, { key: code });
        this.capturing = null;
      } else {
        // An axis capture takes *two* keys, negative then positive. The row says which it wants.
        const pending = this.pendingAxisKeys;
        if (pending === null) {
          this.pendingAxisKeys = { negative: code };
        } else {
          this.options.bindings.setAxis(this.capturing.id, {
            keys: { negative: pending.negative, positive: code },
          });
          this.pendingAxisKeys = null;
          this.capturing = null;
        }
      }
      this.refreshControls();
      return true;
    }

    if (!this.isOpen) return false;
    if (event.key === 'Escape') {
      event.preventDefault();
      if (!this.back()) {
        // At the root of the pause stack, `Esc` resumes; at the main menu it does nothing.
        if (this.screenValue === 'pause') this.options.onResume();
      }
      return true;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      const list = this.lists.get(this.screenValue);
      if (list !== undefined && document.activeElement instanceof HTMLButtonElement) {
        event.preventDefault();
        list.activate();
        return true;
      }
    }
    return false;
  }

  private pendingAxisKeys: { negative: string } | null = null;

  /**
   * Poll the gamepad for menu navigation.
   *
   * The same list the keyboard drives, moved by the same method — so a control that can be reached
   * with the arrow keys can be reached with the stick, by construction rather than by discipline.
   */
  updateFrame(
    pad: { active: boolean; buttons: readonly boolean[]; pitch: number; roll: number },
    dtSeconds: number,
  ): void {
    if (!this.isOpen || !pad.active) return;
    this.padCooldownS = Math.max(0, this.padCooldownS - dtSeconds);

    const list = this.lists.get(this.screenValue);
    if (list === undefined) return;

    // The d-pad and the left stick both navigate; a held stick repeats at 5 Hz rather than 60.
    const down = pad.buttons[13] === true || pad.pitch < -0.6;
    const up = pad.buttons[12] === true || pad.pitch > 0.6;
    if ((down || up) && this.padCooldownS === 0) {
      list.move(down ? 1 : -1);
      this.padCooldownS = 0.2;
    }

    const confirm = pad.buttons[0] === true;
    if (confirm && !this.padWasPressed) list.activate();
    const cancel = pad.buttons[1] === true;
    if (cancel && !this.padWasPressed && !this.back() && this.screenValue === 'pause') {
      this.options.onResume();
    }
    this.padWasPressed = confirm || cancel;
  }

  // --- screens -------------------------------------------------------------------------------

  private makeScreen(id: MenuScreen, title: string, subtitle?: string): HTMLDivElement {
    const screen = document.createElement('div');
    screen.className = 'menu__screen';
    screen.hidden = true;

    const heading = document.createElement('h1');
    heading.className = 'menu__title';
    heading.textContent = title;
    screen.append(heading);

    if (subtitle !== undefined) {
      const sub = document.createElement('p');
      sub.className = 'menu__subtitle';
      sub.textContent = subtitle;
      screen.append(sub);
    }

    this.element.append(screen);
    this.screens.set(id, screen);
    return screen;
  }

  private buildMain(): void {
    // The name comes from `brand.ts` rather than being typed here, so it cannot drift from the
    // browser tab, the watermark, the share text and the Open Graph tags. The subtitle stays
    // local: this one is written for somebody already looking at the menu, and the brand tagline
    // is written for a link preview.
    const screen = this.makeScreen('main', PRODUCT_NAME, this.fleetSubtitle());
    const list = new MenuList();
    list.add('FLY', () => this.options.onFly(), 'take off where you left off');
    /*
     * The fleet belongs on the front door.
     *
     * It used to live only behind pause → AIRCRAFT & MODE, which means the only way to discover
     * that this simulator has more than one aeroplane was to start flying one and then stop. Two
     * aircraft shipped into that arrangement without ever appearing on a screen a first-time
     * visitor sees.
     */
    list.add('AIRCRAFT', () => this.push('aircraft'), this.fleetSummary());
    list.add('CHOOSE AIRPORT', () => this.options.onOpenSpawnPicker(), 'search by ICAO or name');
    list.add('SETTINGS', () => this.push('settings'));
    list.add('CONTROLS', () => this.push('controls'));
    list.add('HELP', () => this.push('help'));
    list.add('CREDITS', () => this.push('credits'));
    screen.append(list.element);
    this.lists.set('main', list);
  }

  private buildPause(): void {
    const screen = this.makeScreen('pause', 'Paused');
    const list = new MenuList();
    list.add('RESUME', () => this.options.onResume());
    list.add('RESTART AT THIS AIRPORT', () => this.options.onRestart());

    /*
     * REARM, where CLAUDE.md asks for it.
     *
     * A full magazine and four fresh missiles, without losing the score or restarting the course —
     * which is the point of putting it here rather than on the respawn: running dry three balloons
     * into a run should not cost the run.
     */
    const rearm = this.options.onRearm;
    if (rearm !== null) {
      this.rearmRow = list.add('REARM', () => {
        rearm();
        this.setRearmNote('reloaded — full gun, four missiles');
      }, 'full gun and four missiles');
    }

    list.add('AIRCRAFT & MODE', () => this.push('aircraft'), this.fleetSummary());
    list.add('SETTINGS', () => this.push('settings'));
    list.add('CONTROLS', () => this.push('controls'));
    list.add('QUIT TO MENU', () => this.options.onQuitToMenu());
    screen.append(list.element);
    this.lists.set('pause', list);
  }

  /** The REARM row, so its hint can confirm the reload. Null when the aircraft is unarmed. */
  private rearmRow: HTMLButtonElement | null = null;

  private setRearmNote(text: string): void {
    const row = this.rearmRow;
    if (row === null) return;
    const hint = row.querySelector('.menu__hint');
    if (hint !== null) hint.textContent = text;
  }

  /**
   * The aircraft and gunnery page.
   *
   * One screen for both because they are the same decision from the pilot's side: what am I
   * flying, and what am I flying *at*. Splitting them puts the fighter two menus away from the
   * only mode that lets it shoot anything.
   */
  private buildAircraft(): void {
    const screen = this.makeScreen('aircraft', 'Aircraft', this.fleetSubtitle());
    screen.append(this.aircraftBody);

    const list = new MenuList('menu__list menu__list--footer');
    list.add('BACK', () => this.back());
    screen.append(list.element);
    this.lists.set('aircraft', list);
  }

  private readonly aircraftBody = document.createElement('div');

  /**
   * Menu copy that counts the fleet, derived rather than typed.
   *
   * Three separate strings named the aircraft — "a study-level aeroplane",
   * "trainer · aerobat · fighter · gunnery", "Trainer, aerobat, or the armed jet" — and all three
   * were describing a three-aeroplane fleet after the fourth and fifth had shipped. Copy that
   * states a fact about the registry should read it.
   */
  private fleetSubtitle(): string {
    const count = this.options.fleet().length;
    return `Real terrain, real airports, ${NUMBER_WORDS[count] ?? String(count)} study-level aeroplanes.`;
  }

  /**
   * The one-line fleet summary: every aeroplane's name, in registry order.
   *
   * Names are left cased as the registry writes them. Every other subtitle on these menus is
   * lower case because every other subtitle is a sentence fragment — "take off where you left
   * off". This one is a list of proper names, and "sr-72 sparrow" is not what the aeroplane is
   * called.
   */
  private fleetSummary(): string {
    return this.options
      .fleet()
      .map((entry) => entry.name)
      .join(' · ');
  }

  /** Rebuild the aircraft page. Cheap, and called only when the screen opens. */
  private refreshAircraft(): void {
    this.aircraftBody.className = 'menu__form';
    this.aircraftBody.replaceChildren();

    const current = this.options.currentAircraftId();
    const heading = document.createElement('h2');
    heading.className = 'menu__section';
    heading.textContent = 'Fleet';
    this.aircraftBody.append(heading);

    const fleet = new MenuList();
    for (const entry of this.options.fleet()) {
      const label = entry.id === current ? `▸ ${entry.name}` : entry.name;
      fleet.add(label.toUpperCase(), () => this.options.onChangeAircraft(entry.id), entry.summary);
    }
    this.aircraftBody.append(fleet.element);

    const modes = document.createElement('h2');
    modes.className = 'menu__section';
    modes.textContent = 'Mode';
    this.aircraftBody.append(modes);

    const modeList = new MenuList();
    const armed = this.options.armed();
    const active = this.options.gunneryActive();
    modeList.add(
      'FREE FLIGHT',
      () => {
        this.options.onGunnery(null);
        this.refreshAircraft();
      },
      active ? 'weapons safe, no targets' : 'current — weapons cold',
    );
    modeList.add(
      'GUNNERY — DRONE RANGE',
      () => {
        this.options.onGunnery('drones');
        this.refreshAircraft();
      },
      armed ? 'a racetrack drone and one that fights back' : 'needs an armed aircraft',
    );
    modeList.add(
      'GUNNERY — BALLOON CANYON',
      () => {
        this.options.onGunnery('balloons');
        this.refreshAircraft();
      },
      armed ? 'six balloons, guns only, against the clock' : 'needs an armed aircraft',
    );
    this.aircraftBody.append(modeList.element);
  }

  private readonly settingsBody = document.createElement('div');

  private buildSettings(): void {
    const screen = this.makeScreen('settings', 'Settings');
    this.settingsBody.className = 'menu__form';
    screen.append(this.settingsBody);

    const list = new MenuList('menu__list menu__list--footer');
    list.add('BACK', () => this.back());
    list.add('RESET TO DEFAULTS', () => {
      this.options.settings.reset();
      this.refreshSettings();
    });
    screen.append(list.element);
    this.lists.set('settings', list);
  }

  private refreshSettings(): void {
    const store = this.options.settings;
    const settings = store.settings;
    this.settingsBody.replaceChildren();

    const section = (title: string): HTMLDivElement => {
      const heading = document.createElement('h2');
      heading.className = 'menu__section';
      heading.textContent = title;
      const body = document.createElement('div');
      body.className = 'menu__rows';
      this.settingsBody.append(heading, body);
      return body;
    };

    const graphics = section('Graphics');
    this.addChoice(graphics, 'Quality', ['auto', ...TIERS] as QualityMode[], settings.qualityMode, (value) =>
      store.patch({ qualityMode: value }),
    );
    this.addSlider(graphics, 'Field of view', settings.fovDeg, 35, 100, 1, '°', (value) =>
      store.patch({ fovDeg: value }),
    );
    this.addSlider(graphics, 'Cockpit field of view', settings.cockpitFovDeg, 35, 100, 1, '°', (value) =>
      store.patch({ cockpitFovDeg: value }),
    );
    this.addSlider(graphics, 'Exposure', settings.exposureBias, 0.5, 2, 0.05, '×', (value) =>
      store.patch({ exposureBias: value }),
    );

    const audio = section('Audio');
    this.addToggle(audio, 'Muted', settings.muted, (value) => store.patch({ muted: value }));
    this.addSlider(audio, 'Master', settings.masterVolume, 0, 1, 0.05, '', (value) =>
      store.patch({ masterVolume: value }),
    );
    this.addSlider(audio, 'Engine', settings.engineVolume, 0, 1, 0.05, '', (value) =>
      store.patch({ engineVolume: value }),
    );
    this.addSlider(audio, 'Wind', settings.windVolume, 0, 1, 0.05, '', (value) =>
      store.patch({ windVolume: value }),
    );
    this.addSlider(audio, 'Ambience', settings.ambienceVolume, 0, 1, 0.05, '', (value) =>
      store.patch({ ambienceVolume: value }),
    );
    this.addSlider(audio, 'Effects', settings.effectsVolume, 0, 1, 0.05, '', (value) =>
      store.patch({ effectsVolume: value }),
    );

    const flying = section('Flying');
    this.addChoice(flying, 'Units', [...UNIT_SYSTEMS] as UnitSystem[], settings.units, (value) =>
      store.patch({ units: value }),
    );
    this.addChoice(flying, 'Starting camera', [...CAMERA_MODES] as CameraMode[], settings.cameraMode, (value) =>
      store.patch({ cameraMode: value }),
    );
    this.addToggle(flying, 'Auto-coordination', settings.coordinationAssist, (value) =>
      store.patch({ coordinationAssist: value }),
    );
    this.addToggle(flying, 'Mouse yoke', settings.mouseYoke, (value) => store.patch({ mouseYoke: value }));
    this.addToggle(flying, 'Fighter HUD overlay', settings.fighterHud, (value) =>
      store.patch({ fighterHud: value }),
    );
    this.addToggle(flying, 'Control hints', settings.helpFooter, (value) =>
      store.patch({ helpFooter: value }),
    );
  }

  private addRow(parent: HTMLElement, label: string): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'menu__row';
    const name = document.createElement('label');
    name.className = 'menu__rowLabel';
    name.textContent = label;
    row.append(name);
    parent.append(row);
    return row;
  }

  private addSlider(
    parent: HTMLElement,
    label: string,
    value: number,
    min: number,
    max: number,
    step: number,
    unit: string,
    onChange: (value: number) => void,
  ): void {
    const row = this.addRow(parent, label);
    const input = document.createElement('input');
    input.type = 'range';
    input.className = 'menu__slider';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(value);

    const readout = document.createElement('span');
    readout.className = 'menu__value';
    const show = (v: number): void => {
      readout.textContent = `${step < 1 ? v.toFixed(2) : v.toFixed(0)}${unit}`;
    };
    show(value);

    // `input` previews while dragging, `change` commits — see `SettingsStore.preview`, which is
    // why a dragged slider does not write to storage sixty times a second.
    input.addEventListener('input', () => show(Number(input.value)));
    input.addEventListener('change', () => onChange(Number(input.value)));
    row.append(input, readout);
  }

  private addToggle(
    parent: HTMLElement,
    label: string,
    value: boolean,
    onChange: (value: boolean) => void,
  ): void {
    const row = this.addRow(parent, label);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'menu__toggle';
    button.setAttribute('aria-pressed', String(value));
    button.textContent = value ? 'ON' : 'OFF';
    button.addEventListener('click', () => {
      const next = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(next));
      button.textContent = next ? 'ON' : 'OFF';
      onChange(next);
    });
    row.append(button);
  }

  private addChoice<T extends string>(
    parent: HTMLElement,
    label: string,
    values: readonly T[],
    current: T,
    onChange: (value: T) => void,
  ): void {
    const row = this.addRow(parent, label);
    const group = document.createElement('div');
    group.className = 'menu__choice';
    for (const value of values) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = value === current ? 'menu__chip menu__chip--active' : 'menu__chip';
      button.textContent = value;
      button.addEventListener('click', () => {
        for (const sibling of group.children) sibling.className = 'menu__chip';
        button.className = 'menu__chip menu__chip--active';
        onChange(value);
      });
      group.append(button);
    }
    row.append(group);
  }

  private buildControls(): void {
    const screen = this.makeScreen(
      'controls',
      'Controls',
      'Click a binding to change it. Axes take two keys — first the negative direction, then the positive.',
    );
    this.controlsBody.className = 'menu__form menu__form--controls';
    screen.append(this.controlsBody);

    const list = new MenuList('menu__list menu__list--footer');
    list.add('BACK', () => this.back());
    list.add('RESET BINDINGS', () => {
      this.options.bindings.reset();
      this.refreshControls();
    });
    screen.append(list.element);
    this.lists.set('controls', list);
  }

  private refreshControls(): void {
    const store = this.options.bindings;
    const clashing = conflicts(store.bindings);
    this.controlsBody.replaceChildren();

    const heading = document.createElement('h2');
    heading.className = 'menu__section';
    heading.textContent = 'Flight axes';
    const axesBody = document.createElement('div');
    axesBody.className = 'menu__rows';
    this.controlsBody.append(heading, axesBody);

    for (const id of AXIS_IDS) {
      const binding = store.axis(id);
      const row = this.addRow(axesBody, AXIS_LABELS[id]);

      const keysButton = document.createElement('button');
      keysButton.type = 'button';
      keysButton.className = 'menu__binding';
      const capturingThis = this.capturing?.kind === 'axis' && this.capturing.id === id;
      keysButton.textContent = capturingThis
        ? this.pendingAxisKeys === null
          ? 'press the negative key…'
          : 'now the positive key…'
        : binding.keys === null
          ? '—'
          : `${keyLabel(binding.keys.negative)} / ${keyLabel(binding.keys.positive)}`;
      keysButton.addEventListener('click', () => {
        this.capturing = { kind: 'axis', id };
        this.pendingAxisKeys = null;
        this.refreshControls();
      });

      const padButton = document.createElement('button');
      padButton.type = 'button';
      padButton.className = 'menu__binding';
      padButton.textContent = gamepadAxisLabel(binding.gamepadAxis);
      padButton.addEventListener('click', () => {
        // Cycle through the four standard axes and off, rather than waiting for a stick to move:
        // a capture that watched for movement would be triggered by the stick's own drift.
        const order: (number | null)[] = [0, 1, 2, 3, null];
        const next = order[(order.indexOf(binding.gamepadAxis) + 1) % order.length];
        store.setAxis(id, { gamepadAxis: next });
        this.refreshControls();
      });

      const invert = document.createElement('button');
      invert.type = 'button';
      invert.className = binding.invert ? 'menu__chip menu__chip--active' : 'menu__chip';
      invert.textContent = 'INVERT';
      invert.addEventListener('click', () => {
        store.setAxis(id, { invert: !binding.invert });
        this.refreshControls();
      });

      const curve = document.createElement('input');
      curve.type = 'range';
      curve.className = 'menu__slider menu__slider--narrow';
      curve.min = '0';
      curve.max = '1';
      curve.step = '0.05';
      curve.value = String(binding.expo);
      curve.title = 'Expo curve — 0 is linear, 1 is fully cubic';
      curve.addEventListener('change', () => {
        store.setAxis(id, { expo: Number(curve.value) });
      });

      const curveLabel = document.createElement('span');
      curveLabel.className = 'menu__value';
      curveLabel.textContent = `curve ${binding.expo.toFixed(2)}`;
      curve.addEventListener('input', () => {
        curveLabel.textContent = `curve ${Number(curve.value).toFixed(2)}`;
      });

      row.append(keysButton, padButton, invert, curve, curveLabel);
    }

    for (const group of ACTION_GROUPS) {
      const groupHeading = document.createElement('h2');
      groupHeading.className = 'menu__section';
      groupHeading.textContent = group.title;
      const body = document.createElement('div');
      body.className = 'menu__rows';
      this.controlsBody.append(groupHeading, body);

      for (const id of group.actions) {
        const binding = store.action(id);
        const row = this.addRow(body, ACTION_LABELS[id]);
        if (clashing.has(id)) row.classList.add('menu__row--clash');

        const keyButton = document.createElement('button');
        keyButton.type = 'button';
        keyButton.className = 'menu__binding';
        const capturingThis = this.capturing?.kind === 'action' && this.capturing.id === id;
        keyButton.textContent = capturingThis ? 'press a key…' : keyLabel(binding.key);
        keyButton.addEventListener('click', () => {
          this.capturing = { kind: 'action', id };
          this.refreshControls();
        });

        const padLabel = document.createElement('span');
        padLabel.className = 'menu__value';
        padLabel.textContent = gamepadButtonLabel(binding.gamepadButton);

        row.append(keyButton, padLabel);
      }
    }
  }

  private buildCredits(): void {
    const screen = this.makeScreen(
      'credits',
      'Credits',
      `${PRODUCT_NAME} runs on free, open data. Attribution is mandatory and it is here.`,
    );
    const body = document.createElement('div');
    body.className = 'menu__prose';
    for (const line of ATTRIBUTIONS) {
      const paragraph = document.createElement('p');
      paragraph.textContent = line;
      body.append(paragraph);
    }
    /*
     * The aircraft, credited by name and by link.
     *
     * All three are CC BY 4.0, which makes this a licence condition rather than good manners — so
     * the artist's name, the work's title and the link to the original are rendered from
     * `models-in/<id>/license.txt` itself. Nothing here is retyped, which is the only way a credit
     * and the model it credits cannot drift apart.
     */
    const aircraftHeading = document.createElement('p');
    aircraftHeading.className = 'menu__creditHeading';
    aircraftHeading.textContent = 'Aircraft models';
    body.append(aircraftHeading);
    for (const credit of MODEL_CREDITS) {
      const paragraph = document.createElement('p');
      const work = document.createElement('a');
      work.href = credit.url;
      work.target = '_blank';
      work.rel = 'noopener noreferrer';
      work.textContent = `“${credit.title}”`;
      const licence = document.createElement('a');
      licence.href = credit.licenceUrl;
      licence.target = '_blank';
      licence.rel = 'noopener noreferrer';
      licence.textContent = credit.licence;
      paragraph.append(work, document.createTextNode(` by ${credit.artist} — `), licence);
      body.append(paragraph);
    }

    const note = document.createElement('p');
    note.className = 'menu__note';
    note.textContent =
      'Sentinel-2 cloudless imagery is licensed for non-commercial use. Built with Three.js. ' +
      'All sound is synthesised; no sampled audio is used.';
    body.append(note);
    screen.append(body);

    const list = new MenuList('menu__list menu__list--footer');
    list.add('BACK', () => this.back());
    screen.append(list.element);
    this.lists.set('credits', list);
  }

  private buildHelp(): void {
    const screen = this.makeScreen('help', 'Controls at a glance', 'The full, rebindable list is under CONTROLS.');
    const body = document.createElement('div');
    body.className = 'menu__helpGrid';
    for (const row of HELP_ROWS) {
      const keys = document.createElement('kbd');
      keys.className = 'menu__kbd';
      keys.textContent = row.keys;
      const label = document.createElement('span');
      label.textContent = row.label;
      body.append(keys, label);
    }
    screen.append(body);

    const list = new MenuList('menu__list menu__list--footer');
    list.add('BACK', () => this.back());
    screen.append(list.element);
    this.lists.set('help', list);
  }

  dispose(): void {
    this.element.remove();
  }
}

/** Re-exported so `main.ts` can apply a settings change without importing the whole store. */
export type { Settings };
